import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface GlowButtonProps {
  variant?: "primary" | "secondary" | "ghost";
  size?: "sm" | "md" | "lg";
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
}

export const GlowButton = ({ 
  className, 
  variant = "primary", 
  size = "md", 
  children,
  onClick,
  disabled,
  type = "button",
}: GlowButtonProps) => {
  const sizeClasses = {
    sm: "px-4 py-2 text-sm",
    md: "px-6 py-3 text-base",
    lg: "h-11 px-8 text-sm",
  };

  const variantClasses = {
    primary: "bg-cta text-cta-foreground hover:bg-cta/90 glow-primary focus:ring-cta",
    secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-border",
    ghost: "bg-transparent text-foreground hover:bg-secondary border border-border hover:border-cream/50 focus:ring-cream/60",
  };

  return (
    <motion.button
      type={type}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "relative inline-flex items-center justify-center gap-2 rounded-lg font-display font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background disabled:opacity-50 disabled:pointer-events-none",
        sizeClasses[size],
        variantClasses[variant],
        className
      )}
    >
      {variant === "primary" && (
        <span className="absolute inset-0 rounded-lg bg-cta opacity-0 blur-xl transition-opacity group-hover:opacity-50" />
      )}
      <span className="relative z-10 flex items-center gap-2">{children}</span>
    </motion.button>
  );
};
